import type { StockRecapSummaryItem } from "@/app/api/types/stock";

/**
 * Aggregated stock summary per product for the recap dropdown.
 *
 * Kept here for backwards-compatibility with the legacy snake_case
 * consumers (recap cells, contexts, product-select). The TanStack Query
 * polling hook (`useGlobalStocks`) that used to live in this file is
 * gone — recap reads stocks reactively from Dexie via
 * `useRecapStocksOffline` (and the stocks page via
 * `useStocksPageOffline`). This file now only exposes the legacy
 * type + the helper, both of which downstream callers still consume.
 */
export interface StockSummary {
  quantity: number;
  weight: number;
  unit_id: string | null;
  /**
   * Generic numeric metadata map keyed by stock_metadata field key (e.g.
   * `available`, `j1`..`j5`). Every numeric (or numeric-string) value found
   * on `stock_metadata` is copied here — no hardcoded key list. Dropdown
   * columns are config-driven via `resolveStockDropdownColumns`.
   */
  metadata: Record<string, number | null>;
}

/**
 * Build the numeric metadata map from a raw stock_metadata blob: copy every
 * numeric (or numeric-string) value via `parseNumber`, dropping non-numeric
 * entries.
 */
function buildMetadataMap(
  meta: Record<string, unknown> | null,
): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  if (!meta) return out;
  for (const [key, value] of Object.entries(meta)) {
    const num = parseNumber(value);
    if (num !== null) out[key] = num;
  }
  return out;
}

/**
 * Build a Map<productId, StockSummary> from raw stock items.
 * Exported so callers can build filtered maps (e.g. by location).
 */
export function buildStockMapFromItems(
  items: StockRecapSummaryItem[],
): Map<string, StockSummary> {
  const map = new Map<string, StockSummary>();
  for (const item of items) {
    const existing = map.get(item.product_id);
    const meta = item.stock_metadata as Record<string, unknown> | null;

    if (existing) {
      existing.quantity += item.quantity;
      existing.weight += item.weight;
      // Sum metadata across rows (lots). A product can have several stock rows
      // (one per lot/location); the configured stock columns (`available`,
      // `j1`..`j5`) are additive quantities, so a product's value is the sum
      // across its lots. RHS has exactly one lot per product (sum == the single
      // value, so this is a no-op there), but multi-lot tenants need the sum.
      // The AI side (`_summarize_stocks` / `_format_stocks_section`) already
      // sums these columns — the dropdown must agree with what the agent sees.
      const incoming = buildMetadataMap(meta);
      for (const [key, value] of Object.entries(incoming)) {
        if (value === null) continue;
        existing.metadata[key] = (existing.metadata[key] ?? 0) + value;
      }
    } else {
      map.set(item.product_id, {
        quantity: item.quantity,
        weight: item.weight,
        unit_id: item.unit_id,
        metadata: buildMetadataMap(meta),
      });
    }
  }
  return map;
}

function parseNumber(v: unknown): number | null {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    // Empty / whitespace-only strings are NOT numbers — skip them. JS's
    // `Number("")` / `Number("  ")` coerce to 0, which would surface a phantom
    // 0 the agent omits (its `coerce_stock_number` skips blanks). Trim first so
    // the two sides stay in parity on summed stock columns.
    const s = v.trim();
    if (s === "") return null;
    const n = Number(s);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}
